import {Box, Button, Paper, Stack, Typography} from '@mui/material';
import RichEditor from './Shared/RichEditor';
import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import RichEditorResultText from './Shared/RichEditorResultText';
import {stripHtmlTags} from './AdditionalNotes';
import {useDropzone} from 'react-dropzone';
import useNotification from '../../../../../utility/hooks/useNotification';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileRounded';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';

const MAX_FORM_FILE_UPLOAD_SIZE_IN_MB = 10;

const FileUploader = ({
                          index,
                          id,
                          value,
                          onChange,
                          description,
                          isEditMode,
                          enableValidation,
                          isFormArchived,
                          readonly,
                          placeholderValues,
                          accessLevel,
                          handleDownloadAttachment,
                          error,
                          submitMode,
                          audience,
                          recipientType,
                      }) => {
    const [componentDescription, setComponentDescription] = useState(description ?? '');
    const {showErrorNotification} = useNotification();

    useEffect(() => {
        setComponentDescription(description ?? '');
    }, [description]);

    const handleDescriptionChange = (nextValue) => {
        setComponentDescription(nextValue);

        if (!onChange) return;

        onChange({
            index,
            description: stripHtmlTags(nextValue),
            formattedDescription: nextValue,
        });
    };

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length) {
            onChange({
                id,
                value: acceptedFiles[0]
            });
        }
    }, [id, onChange]);

    const onDropRejected = useCallback(
        (rejections) => {
            const oversized = rejections.some((r) =>
                r.errors.some((e) => e.code === 'file-too-large'),
            );
            const badType = rejections.some((r) =>
                r.errors.some((e) => e.code === 'file-invalid-type'),
            );

            if (oversized) {
                showErrorNotification(
                    `The file exceeds the ${MAX_FORM_FILE_UPLOAD_SIZE_IN_MB} MB limit.`,
                );
            }
            if (badType) {
                showErrorNotification(
                    'Unsupported file type.',
                );
            }
        },
        [showErrorNotification],
    );

    const {getInputProps, open} = useDropzone({
        onDrop,
        onDropRejected,
        noClick: true,
        noKeyboard: true,
        accept: {
            'image/*': [],
            'application/msword': [],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
            'application/pdf': [],
            'application/vnd.ms-excel': [],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
            'application/zip': [],
            'application/x-zip-compressed': [],
            'application/vnd.ms-powerpoint': [],
            'application/vnd.openxmlformats-officedocument.presentationml.presentation': [],
        },
        multiple: false,
        maxSize: MAX_FORM_FILE_UPLOAD_SIZE_IN_MB * 1024 * 1024,
    });

    return (
        <Box display="flex" flexDirection="column" gap={2} sx={{my: 2}}>
            {isEditMode
                ? (
                    <RichEditor
                        value={description}
                        onChange={onChange}
                        index={index}
                        isFormLive={isFormArchived}
                        accessLevel={accessLevel}
                        currentDescription={componentDescription}
                        setCurrentDescription={handleDescriptionChange}
                        audience={audience}
                        recipientType={recipientType}
                    />
                )
                : (
                    <RichEditorResultText
                        id={id}
                        description={description}
                        placeholderValues={placeholderValues}
                        readonly={readonly}
                        enableValidation={enableValidation}
                    />
                )}

            {value
                ? (
                    <Paper variant="outlined" sx={{width: 'fit-content'}}>
                        <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            alignItems="center"
                            paddingTop={0.5}
                            paddingBottom={0.5}
                            paddingLeft={1}
                            paddingRight={1}
                            gap={1}
                        >
                            <InsertDriveFileRoundedIcon
                                fontSize="small"
                                sx={{color: 'primary.main'}}
                            />
                            <Typography variant="body2">{value.name}</Typography>
                            {!readonly
                                ? (
                                    <IconButton
                                        size="small"
                                        sx={{padding: 0.25}}
                                        onClick={() => onChange({
                                            id,
                                            value: null
                                        })}
                                    >
                                        <CloseIcon fontSize="small"/>
                                    </IconButton>
                                )
                                : value.fileKey && handleDownloadAttachment
                                    ? (
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDownloadAttachment(value.fileKey)}
                                        >
                                            <DownloadRoundedIcon fontSize="small"/>
                                        </IconButton>
                                    )
                                    : null}
                        </Stack>
                    </Paper>
                )
                : !readonly
                    ? (
                        <FormControl error={!!error} variant="standard">
                            <Stack flexDirection="row" alignItems="center" gap={1}>
                                <input {...getInputProps()} />
                                <Button
                                    onClick={open}
                                    disabled={!submitMode}
                                    startIcon={<UploadFileOutlinedIcon/>}
                                    variant="outlined"
                                >
                                    Add File
                                </Button>
                                <Typography
                                    variant="body2"
                                    color={!submitMode ? 'text.disabled' : 'text.secondary'}
                                >
                                    Max file size {MAX_FORM_FILE_UPLOAD_SIZE_IN_MB} MB
                                </Typography>
                            </Stack>
                            <FormHelperText>{error}</FormHelperText>
                        </FormControl>
                    )
                    : null}

            {isEditMode && (
                <RequiredCheckbox
                    enableValidation={enableValidation}
                    index={index}
                    onChange={onChange}
                    isFormArchived={isFormArchived}
                />
            )}
        </Box>
    );
};

FileUploader.propTypes = {
    id: PropTypes.string,
    value: PropTypes.any,
    index: PropTypes.number,
    options: PropTypes.array,
    description: PropTypes.string,
    enableValidation: PropTypes.bool,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    relatedComponent: PropTypes.object,
    readonly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    onUpdateDescription: PropTypes.func,
    componentToEditIndex: PropTypes.number,
    handleDownloadAttachment: PropTypes.func,
    error: PropTypes.string,
    submitMode: PropTypes.bool,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
};

export default FileUploader;
